import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { AppMark } from "@/components/kb/app-mark";
import { PageHeader } from "@/components/kb/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type FeaturedApp, featuredApps } from "@/lib/featured-apps";
import { getInstalledApps, isNativeShell, openExternal } from "@/lib/native";
import { rootRoute } from "./__root";

function AppsComponent() {
	const native = isNativeShell();
	const [installed, setInstalled] = useState<Record<string, boolean> | null>(
		null,
	);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		if (!native) {
			return;
		}

		let cancelled = false;

		getInstalledApps(featuredApps.flatMap((app) => app.schemes))
			.then((result) => {
				if (!cancelled) {
					setInstalled(result);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setFailed(true);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [native]);

	const openApp = useCallback(
		(app: FeaturedApp) => {
			const fallbackUrl = app.appStoreUrl ?? app.webUrl;
			const scheme = app.schemes.find((candidate) => installed?.[candidate]);

			if (native && scheme) {
				openExternal(scheme, fallbackUrl).catch(() => {
					window.open(fallbackUrl, "_blank", "noopener,noreferrer");
				});
				return;
			}

			window.open(fallbackUrl, "_blank", "noopener,noreferrer");
		},
		[installed, native],
	);

	const isChecking = native && !failed && installed === null;
	const visibleApps =
		!native || failed || installed === null
			? featuredApps
			: featuredApps.filter((app) =>
					app.schemes.some((scheme) => installed[scheme]),
				);

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				description={
					native
						? "Fantasy sports and sportsbook apps installed on this device."
						: "Fantasy sports and sportsbook apps. Open Knowsball on your phone to launch these directly."
				}
				title="Apps"
			/>

			{isChecking ? (
				<div className="flex flex-col gap-3">
					<Skeleton className="h-[4.5rem] w-full rounded-xl" />
					<Skeleton className="h-[4.5rem] w-full rounded-xl" />
					<Skeleton className="h-[4.5rem] w-full rounded-xl" />
				</div>
			) : visibleApps.length === 0 ? (
				<div className="rounded-xl border border-dashed px-6 py-16 text-center text-sm text-muted-foreground">
					None of these apps are installed on this device.
				</div>
			) : (
				<div className="grid gap-3 sm:grid-cols-2">
					{visibleApps.map((app) => {
						const installedScheme = app.schemes.find(
							(scheme) => installed?.[scheme],
						);

						return (
							<article
								className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-xs"
								key={app.id}
							>
								<AppMark app={app} size="lg" />
								<div className="min-w-0 flex-1">
									<h2 className="font-medium">{app.name}</h2>
									<p className="text-sm leading-snug text-muted-foreground">
										{app.description}
									</p>
								</div>
								<Button
									onClick={() => openApp(app)}
									size="sm"
									variant="outline"
								>
									{native && installedScheme ? "Open" : "Get"}
								</Button>
							</article>
						);
					})}
				</div>
			)}

			<div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground shadow-xs">
				<p className="font-medium text-foreground">
					Alerts are push-only for now.
				</p>
				<p className="mt-1">
					You can launch an app here. App shortcuts from alerts are coming
					later.
				</p>
			</div>
		</div>
	);
}

export const appsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/apps",
	component: AppsComponent,
});
